import React, { useCallback, useEffect, useRef, useState } from "react";
import { getGeocode } from "use-places-autocomplete";

import { CircularIndeterminate, FormButton, Mapa } from "components";
import { FormikAutocomplete, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { accesoUpd, localidadGet } from "services/services-mia/miakey";
import ModalMapa from "components/utils/mapa/ModalMapa";
import { Grid } from "@mui/material";
import Modal from "components/UI/Modal/Modal";
import { inputTextRegexp } from "utils/regexp";
import { AddLocationAltOutlined } from "@mui/icons-material";

export default function UpdateModal({ updateList, acceso, closeModal, zonas = [], provincias = [] }) {
    const [inRequest, setInRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localidades, setLocalidades] = useState([]);
    const [modalMapaOpen, setModalMapaOpen] = useState(false);
    const [coordenadas, setCoordenadas] = useState({});
    const lastGeocodedRef = useRef("");

    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const formikRef = useRef();

    const normalizeNombre = (str) =>
        str?.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim() ?? "";

    const matchesNombre = (nombre, termino) => {
        const a = normalizeNombre(nombre);
        const b = normalizeNombre(termino);
        return a.includes(b) || b.includes(a);
    };

    function GeocodeWatcher() {
        const { values } = useFormikContext();
        const isFirstRender = useRef(true);

        useEffect(() => {
            if (isFirstRender.current) {
                isFirstRender.current = false;
                return;
            }
            let mounted = true;
            const calle = (values?.calle || "").trim();
            const numero = (values?.numero || "").toString().trim();
            const localidadName = values?.localidadNombre || "";
            const provinciaName = values?.provinciaNombre || "";

            const hasCalle = calle.length >= 3;
            const hasLocalidad = Boolean(localidadName);
            if (!hasCalle && !hasLocalidad) return;

            const parts = [];
            if (hasCalle) parts.push(numero ? `${calle} ${numero}` : calle);
            if (localidadName) parts.push(localidadName);
            if (provinciaName) parts.push(provinciaName);
            parts.push("Argentina");
            const composed = parts.join(", ");
            if (composed === lastGeocodedRef.current) return;

            const timer = setTimeout(async () => {
                if (!mounted) return;
                try {
                    const results = await getGeocode({ address: composed });
                    if (results && results[0]) {
                        const lat = results[0].geometry.location.lat();
                        const lng = results[0].geometry.location.lng();
                        setCoordenadas({ latitud: lat, longitud: lng });
                        if (formikRef.current) {
                            formikRef.current.setFieldValue("latitud", Number(lat).toFixed(6));
                            formikRef.current.setFieldValue("longitud", Number(lng).toFixed(6));
                        }
                        lastGeocodedRef.current = composed;
                    }
                } catch (err) {
                    console.error("Geocode error:", err);
                }
            }, 1000);

            return () => {
                mounted = false;
                clearTimeout(timer);
            };
        }, [values?.calle, values?.numero, values?.localidadNombre, values?.provinciaNombre]);

        return null;
    }

    const getLocalidades = useCallback(async (provinciaId, { selectFirst = false } = {}) => {
        if (!provinciaId) return;
        setIsLoading(true);
        const { result, status } = await localidadGet({ provinciaId }, credentials);
        if (status.code !== 1) {
            setIsLoading(false);
            return;
        }
        setLocalidades(result);
        if (selectFirst && result[0]) selectLocalidad(result[0]);
        setIsLoading(false);
    }, [credentials]);

    useEffect(() => {
        if (Object.keys(credentials).length === 0) return;
        setCoordenadas({
            latitud: acceso.gateLugarLatitud ?? -34.6036844,
            longitud: acceso.gateLugarLongitud ?? -58.3815591,
        });
        if (acceso.provinciaId) getLocalidades(acceso.provinciaId);
    }, [credentials, acceso, getLocalidades]);

    const selectLocalidad = ({ localidadId, localidadNombre }) => {
        formikRef.current.setFieldValue("localidad", localidadId);
        formikRef.current.setFieldValue("localidadNombre", localidadNombre);
    };

    const handleChangeProvincia = (selected) => {
        formikRef.current.setFieldValue("localidad", "");
        formikRef.current.setFieldValue("localidadNombre", "");
        formikRef.current.setFieldValue("provinciaNombre", selected?.provinciaNombre ?? "");
        setLocalidades([]);
        if (selected?.provinciaId) {
            getLocalidades(selected.provinciaId, { selectFirst: true });
        }
    };

    const handleSetCoordenadas = ({ latitud, longitud, calle, numero, localidad }) => {
        setCoordenadas({ latitud, longitud });
        if (latitud) formikRef.current.setFieldValue("latitud", latitud.toFixed(6));
        if (longitud) formikRef.current.setFieldValue("longitud", longitud.toFixed(6));
        if (calle) formikRef.current.setFieldValue("calle", calle);
        if (numero) formikRef.current.setFieldValue("numero", numero);
        // El mapa devuelve el codigo postal bajo la clave `cod`, pero aca el campo
        // "cod" del form es gateLugarCod (referencia del acceso), no la ubicacion.
        // Por eso no se pisa. En SFHoteles si corresponde: alli mapea a SFHotelDomicilioCPA.
        if (localidad) {
            const currentLocalidad = localidades.find((l) =>
                matchesNombre(l.localidadNombre, localidad)
            );
            if (currentLocalidad) selectLocalidad(currentLocalidad);
        }
    };

    const handleSubmit = async ({
        zona, nombre, calle, localidad, numero, unidad, piso, radio, cod
    }) => {
        setInRequest(true);

        const response = await accesoUpd({
            gateLugarId: acceso.gateLugarId,
            gateZonaId: zona,
            gateLugarNombre: nombre,
            gateLugarCalle: calle,
            localidadId: localidad,
            gateLugarCasa: numero,
            gateLugarUnidad: unidad,
            gateLugarPiso: piso,
            gateLugarMinimo: 0,
            gateLugarMaximo: 99999,
            gateLugarLatitud: coordenadas.latitud.toFixed(6),
            gateLugarLongitud: coordenadas.longitud.toFixed(6),
            gateLugarRadio: Number(radio),
            gateLugarCod: cod,
            gateLugarVersion: acceso.gateLugarVersion,
        }, credentials);

        handleResponse(response, "Acceso modificado exitosamente", {
            setSnackbarInfo,
            setIsOpen: closeModal,
            setSelected,
            updateList,
        });

        setInRequest(false);
    };

    const closeModalMapa = () => setModalMapaOpen(false);

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                id: acceso.id,
                zona: acceso.gateZonaId,
                nombre: acceso.gateLugarNombre ?? "",
                provincia: acceso.provinciaId ?? "",
                provinciaNombre: acceso.provinciaNombre ?? "",
                localidad: acceso.localidadId ?? "",
                localidadNombre: acceso.localidadNombre ?? "",
                calle: acceso.gateLugarCalle ?? "",
                numero: acceso.gateLugarCasa ?? "",
                piso: acceso.gateLugarPiso ?? "",
                unidad: acceso.gateLugarUnidad ?? "",
                radio: acceso.gateLugarRadio ?? "",
                cod: acceso.gateLugarCod ?? "",
                latitud: acceso.gateLugarLatitud ? Number(acceso.gateLugarLatitud).toFixed(6) : "",
                longitud: acceso.gateLugarLongitud ? Number(acceso.gateLugarLongitud).toFixed(6) : "",
            }}
            validationSchema={yup.object({
                zona: yup.number(FORM_ERROR_MESSAGES.number)
                    .required(FORM_ERROR_MESSAGES.required),
                nombre: yup.string(FORM_ERROR_MESSAGES.text)
                    .min(2, "Debe tener minimo 2 caracteres")
                    .max(150, "Debe tener un maximo de 150 caracteres")
                    .matches(inputTextRegexp, "Debe tener solo letras y/o números")
                    .required(FORM_ERROR_MESSAGES.required),
                provincia: yup.string(FORM_ERROR_MESSAGES.text)
                    .required(FORM_ERROR_MESSAGES.required)
                    .min(1, FORM_ERROR_MESSAGES.required),
                localidad: yup.string(FORM_ERROR_MESSAGES.text)
                    .required(FORM_ERROR_MESSAGES.required)
                    .min(1, FORM_ERROR_MESSAGES.required),
                calle: yup.string(FORM_ERROR_MESSAGES.text)
                    .min(2, "Debe tener minimo 2 caracteres")
                    .max(45, "Debe tener un maximo de 45 caracteres")
                    .matches(inputTextRegexp, "Debe tener solo letras y/o números")
                    .required(FORM_ERROR_MESSAGES.required),
                numero: yup.string(FORM_ERROR_MESSAGES.text)
                    .required(FORM_ERROR_MESSAGES.required),
                radio: yup.number(FORM_ERROR_MESSAGES.number)
                    .required(FORM_ERROR_MESSAGES.required)
                    .typeError(FORM_ERROR_MESSAGES.number),
                cod: yup.string(FORM_ERROR_MESSAGES.text)
                    .max(15, "Debe tener un maximo de 15 caracteres")
                    .matches(inputTextRegexp, "Debe tener solo letras y/o números")
                    .required(FORM_ERROR_MESSAGES.required),
            })}
            onSubmit={(values, { setSubmitting }) => {
                try {
                    handleSubmit(values);
                } catch (e) {
                    setSnackbarInfo({ message: e.errmsg, severity: "error", open: true });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Form className={styles.inputs} noValidate>
                <GeocodeWatcher />
                <Modal onClose={closeModalMapa} open={modalMapaOpen} title="Seleccionar coordenadas">
                    {modalMapaOpen && (
                        <ModalMapa
                            closeModal={closeModalMapa}
                            latitud={coordenadas.latitud}
                            longitud={coordenadas.longitud}
                            calle={formikRef?.current?.values?.calle}
                            numero={formikRef?.current?.values?.numero}
                            localidad={formikRef?.current?.values?.localidadNombre}
                            provincia={formikRef?.current?.values?.provinciaNombre}
                            cod={formikRef?.current?.values?.cod}
                            onSetCoordenadas={handleSetCoordenadas}
                        />
                    )}
                </Modal>
                <div className={styles.column}>
                    <Grid container spacing={2} sx={{ alignItems: "stretch" }}>

                        {/* Columna izquierda */}
                        <Grid item md={6}>
                            <Grid container spacing={2}>

                                {/* Zona | Código */}
                                <Grid item md={6}>
                                    <FormikAutocomplete
                                        name="zona"
                                        labelText="Zona"
                                        options={zonas}
                                        optionId="gateZonaId"
                                        optionLabel="gateZonaNombre"
                                        placeholder="Seleccione una zona"
                                        formik={formikRef.current}
                                    />
                                </Grid>
                                <Grid item md={6}>
                                    <FormikTextInput
                                        name="cod"
                                        labelText="Código"
                                        placeholder="Ingrese el código"
                                    />
                                </Grid>

                                {/* Nombre */}
                                <Grid item md={12}>
                                    <FormikTextInput
                                        name="nombre"
                                        labelText="Nombre"
                                        placeholder="Ingrese nombre"
                                    />
                                </Grid>

                                {/* Provincia | Localidad */}
                                <Grid item md={6}>
                                    <FormikAutocomplete
                                        name="provincia"
                                        labelText="Provincia"
                                        options={provincias}
                                        optionId="provinciaId"
                                        optionLabel="provinciaNombre"
                                        placeholder="Seleccionar provincia"
                                        formik={formikRef.current}
                                        onChange={handleChangeProvincia}
                                    />
                                </Grid>
                                <Grid item md={6}>
                                    <FormikAutocomplete
                                        name="localidad"
                                        labelText="Localidad"
                                        options={localidades}
                                        optionId="localidadId"
                                        optionLabel="localidadNombre"
                                        placeholder="Seleccionar provincia"
                                        formik={formikRef.current}
                                        onChange={(selected) => {
                                            formikRef.current.setFieldValue("localidadNombre", selected?.localidadNombre ?? "");
                                        }}
                                    />
                                </Grid>

                                {/* Calle */}
                                <Grid item md={12}>
                                    <FormikTextInput name="calle" labelText="Calle" />
                                </Grid>

                                {/* Número | Piso | Unidad */}
                                <Grid item md={4}>
                                    <FormikTextInput name="numero" labelText="Número" />
                                </Grid>
                                <Grid item md={4}>
                                    <FormikTextInput name="piso" labelText="Piso" />
                                </Grid>
                                <Grid item md={4}>
                                    <FormikTextInput name="unidad" labelText="Unidad" />
                                </Grid>

                            </Grid>
                        </Grid>

                        {/* Columna derecha — mapa + radio/coords */}
                        <Grid item md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            {/* Mapa — crece para ocupar el espacio restante */}
                            <div style={{ flex: 1, minHeight: 0, marginBottom: 16 }}>
                                <div className={styles.mapPanel} style={{ height: "100%" }}>
                                    <div className={styles.mapContainer}>
                                        {!modalMapaOpen && (
                                            <Mapa
                                                lat={coordenadas.latitud}
                                                lng={coordenadas.longitud}
                                                style={{ position: "absolute", inset: 0 }}
                                            />
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="map-btn"
                                        onClick={() => setModalMapaOpen(true)}
                                        style={{ margin: "0.5rem", border: "none", display: "flex", alignItems: "center", gap: "0.2rem", backgroundColor: "transparent", cursor: "pointer" }}
                                    >
                                        <AddLocationAltOutlined sx={{ width: "18px", height: "18px" }} />
                                        <span style={{ borderBottom: "1px solid #115D8D", fontWeight: "bold" }}>
                                            Buscar coordenadas
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Radio | Latitud | Longitud — fija al fondo */}
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <FormikTextInput
                                        name="radio"
                                        labelText="Radio"
                                        placeholder="Distancia (m)"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormikTextInput name="latitud" labelText="Latitud" isViewDetail={true} />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormikTextInput name="longitud" labelText="Longitud" isViewDetail={true} />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                    <FormButton
                        inRequest={inRequest || isLoading}
                        newFormData={{ button: "Guardar cambios" }}
                        onCloseModal={closeModal}
                    />
                    {(inRequest || isLoading) && <CircularIndeterminate />}
                </div>
            </Form>
        </Formik>
    );
}
